import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  OutlinedInput,
  FormControl,
  InputLabel,
  FormHelperText,
  keyframes,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { authAPI } from "../API/Auth";
import { tokens } from "../theme/tokens";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Email, 2: OTP and New Password
  const [countdown, setCountdown] = useState(0);
  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    otp: "",
    password: "",
    confirmPassword: "",
  });

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [countdown]);

  const startCountdown = () => {
    setCountdown(60); // 60 seconds countdown
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setErrors((prev) => ({
        ...prev,
        email: "Email is required",
      }));
      return;
    }

    if (!validateEmail(formData.email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Please enter a valid email address",
      }));
      return;
    }

    setIsLoading(true);
    try {
      const response = await authAPI.sendOTP(formData.email);
      if (!response.error) {
        toast.success("OTP sent successfully!");
        setStep(2);
        startCountdown(); // Start countdown when OTP is sent
      }
    } catch (error) {
      toast.error(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) {
      toast.info(`Please wait ${countdown} seconds before requesting a new OTP`);
      return;
    }

    setIsLoading(true);
    try {
      const response = await authAPI.sendOTP(formData.email);
      if (!response.error) {
        toast.success("OTP resent successfully!");
        startCountdown(); // Start countdown when OTP is resent
      }
    } catch (error) {
      toast.error(error.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!formData.otp.trim()) {
      setErrors((prev) => ({
        ...prev,
        otp: "OTP is required",
      }));
      return;
    }

    if (!formData.password) {
      setErrors((prev) => ({
        ...prev,
        password: "Password is required",
      }));
      return;
    }

    if (formData.password.length < 6) {
      setErrors((prev) => ({
        ...prev,
        password: "Password must be at least 6 characters",
      }));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      return;
    }

    setIsLoading(true);
    try {
      const response = await authAPI.forgotPassword({
        email: formData.email,
        otp: formData.otp,
        password: formData.password
      });
      if (!response.error) {
        toast.success("Password reset successfully!");
        navigate("/login");
      }
    } catch (error) {
      toast.error(error.message || "Invalid OTP or failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fadeIn = keyframes`
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  `;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(135deg, ${tokens.brandDeep} 0%, ${tokens.brand} 40%, ${tokens.accent} 100%)`,
      }}
    >
      <ToastContainer />
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 4 },
            width: "100%",
            maxWidth: { xs: "90%", sm: 380 },
            minHeight: { xs: "auto", sm: 350 },
            textAlign: "center",
            borderRadius: { xs: "16px", sm: "20px" },
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
            mx: "auto",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <img src="/images/Wordmark.svg" alt="Hotel Logo" height={30} />
          </Box>

          <Typography variant="h4" sx={{ mb: 4 }}>
            {step === 1 ? "Forgot Password" : "Enter OTP"}
          </Typography>

          <Box component="form">
            {step === 1 && (
              <>
                <FormControl fullWidth error={!!errors.email} sx={{ mb: 3 }}>
                  <InputLabel>Email</InputLabel>
                  <OutlinedInput
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <FormHelperText>{errors.email}</FormHelperText>
                  )}
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSendOTP}
                  disabled={isLoading}
                  sx={{ mb: 2, borderRadius: { xs: "25px", sm: "30px" }, p: 1.5 }}
                >
                  {isLoading ? <CircularProgress size={24} /> : "Send OTP"}
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <FormControl fullWidth error={!!errors.otp} sx={{ mb: 3 }}>
                  <InputLabel>OTP</InputLabel>
                  <OutlinedInput
                    label="OTP"
                    name="otp"
                    value={formData.otp}
                    onChange={handleChange}
                  />
                  {errors.otp && (
                    <FormHelperText>{errors.otp}</FormHelperText>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  sx={{ mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  sx={{ mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleVerifyOTP}
                  disabled={isLoading}
                  sx={{ mb: 2, borderRadius: { xs: "25px", sm: "30px" }, p: 1.5 }}
                >
                  {isLoading ? <CircularProgress size={24} /> : "Reset Password"}
                </Button>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Didn't receive the OTP?
                  </Typography>
                  <Button
                    onClick={handleResendOTP}
                    disabled={countdown > 0 || isLoading}
                    sx={{
                      color: tokens.brand,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {countdown > 0
                      ? `Resend OTP in ${countdown}s`
                      : "Resend OTP"}
                  </Button>
                </Box>
              </>
            )}

            <Box sx={{ mt: 1, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">
                Remember your password?{" "}
                <Button
                  onClick={() => navigate("/login")}
                  sx={{
                    padding: { xs: "10px", sm: "12px" },
                    fontSize: { xs: "14px", sm: "16px" },
                    fontWeight: 500,
                    borderRadius: { xs: "25px", sm: "30px" },
                    color: tokens.brand,
                  }}
                >
                  Login
                </Button>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword; 